<template>
  <div v-on-click-outside="closePanel" :class="hasBorders ? $style.inputContainer : $style.itemsContainer">
    <div :class="$style.displayItems" @click="hasBorders && togglePanel()">
      <div v-if="visibleItems.length > 0" :class="$style.chipContainer">
        <Chip
          v-for="(item, index) in visibleItems"
          :key="index"
          :label="item.storeItemName"
          class="p-mr-2"
          :class="$style.chip"
        />
        <Chip v-if="hasMore" :label="`+${remainingCount}`" class="p-mr-2" :class="$style.chip" />
      </div>
      <div v-else>None</div>
      <Button
        icon="pi pi-chevron-down"
        class="p-button-rounded p-button-text"
        :disabled="disabled"
        :class="$style.dropdownButton"
        @click.stop="togglePanel"
      />
    </div>
    <VTMultiSelect
      v-if="showPanel"
      :items="listItems"
      :selected-items="selectedItems"
      :class="$style.itemsSelector"
      :allow-deletion="false"
      :has-add-section="false"
      selection-key="storeItemId"
      info-message="Once published, the IAP will be locked and associations with different non-consumables won’t be possible"
      @toggle-selection="handleToggleItemSelection"
    />
  </div>
</template>

<script setup>
import VTMultiSelect from "@/components/VTMultiSelect";
import { useUpdateIapMappingMutation } from "@/graphql";
import { useItems } from "@/modules/Items/hooks/useItems";
import { vOnClickOutside } from "@vueuse/components";
import Button from "primevue/button";
import Chip from "primevue/chip";
import { computed, ref } from "vue";

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
  gameId: String,
  hasBorders: Boolean,
  type: String,
  versionId: String,
  maxVisibleItems: {
    type: Number,
    default: 4,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  iapId: String,
});

const showPanel = ref(false);

const { nonConsumableItems } = useItems(props.gameId, props.versionId);
const { mutate: updateIAPMapping } = useUpdateIapMappingMutation({});

const listItems = computed(() =>
  nonConsumableItems.value?.map((item) => ({ name: item.name, storeItemId: item.id, isDeletable: false }))
);

const selectedItems = ref(props.modelValue);

const visibleItems = computed(() => selectedItems.value.slice(0, props.maxVisibleItems));
const remainingCount = computed(() => selectedItems.value.length - props.maxVisibleItems);
const hasMore = computed(() => selectedItems.value.length > props.maxVisibleItems);

function togglePanel() {
  showPanel.value = !showPanel.value;
}

function closePanel() {
  showPanel.value = false;
}

const handleToggleItemSelection = async (item) => {
  const formattedNewItem = {
    storeItemName: item.name,
    storeItemId: item.storeItemId,
    isDeletable: false,
  };

  const index = selectedItems.value.findIndex(
    (selectedItem) => selectedItem.storeItemName === formattedNewItem.storeItemName
  );

  if (index > -1) {
    const newItems = [...selectedItems.value];
    newItems.splice(index, 1);
    selectedItems.value = newItems;
  } else {
    selectedItems.value = [...selectedItems.value, formattedNewItem];
  }

  const newIds = selectedItems.value.map((selectedItem) => selectedItem.storeItemId);

  await updateIAPMapping({
    iapId: props.iapId,
    gameId: props.gameId,
    versionId: props.versionId,
    storeItemIds: newIds,
  });
};
</script>

<style module lang="scss">
.chip {
  margin-left: 5px;
}

.chipContainer {
  display: flex;
  width: 100%;
}

.itemsContainer {
  display: flex;
  flex-direction: column;
  position: relative;
  font-weight: 400;
  width: 100%;

  .displayItems {
    display: flex;
    align-items: center;
    gap: 5px;
    justify-content: space-between;
    width: 100%;
  }

  .itemsSelector {
    position: absolute;
    width: 100%;
    top: 100%;
    left: 0;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    border: 1px solid #d1d5db;
    min-width: 260px;
  }
}

.inputContainer {
  display: flex;
  position: relative;
  font-weight: 400;
  height: 40px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  width: 100%;

  .chip {
    font-size: 12px;
  }
}
</style>

<style scoped>
:deep(.p-chip) {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 180px;
}
</style>
